import React, { useEffect } from 'react';

import { FlowValues, StepProps, track } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Box, Button, Heading } from '@betterment/style-closet-react';
import { startCase as _startCase, toLower as _toLower } from 'es-toolkit/compat';
import { Form, Formik } from 'formik';

import { CustomerFlowValues } from './Controller';

import { BankDocument, User } from '../Checking/data';
import { isManualReview, isVerified } from '../Checking/models/customer';
import { trackClick } from '../features/track';
import brochureUrl from '../Shared/brochureUrl';
import FlashErrorMessage from '../Shared/components/FlashErrorMessage';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { WorkflowLayout } from '../Shared/layout/WorkflowLayout';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

const DocumentLink: React.FC<React.PropsWithChildren<BankDocument>> = ({ id, title }) => (
  <SambaAutoLink
    target="_blank"
    to={`${urlBuilder.getBankDocuments()}/${id}`}
    onClick={() => trackClick({ name: 'UserAgreement' })}
  >
    {_startCase(_toLower(title))}
  </SambaAutoLink>
);

const BettermentAgreements: React.FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <>
      <Text
        as="p"
        css={{
          fontWeight: 'bold',
          marginBottom: 'space.100',
        }}
      >
        BETTERMENT AGREEMENTS
      </Text>
      <Box as="ul" m="0" mb="6" pl="7">
        <li>
          <SambaAutoLink
            to={brochureUrl('/customeragreement')}
            target="_blank"
            onClick={() => trackClick({ name: 'UserAgreement' })}
          >
            Customer Agreements
          </SambaAutoLink>
        </li>
        <li>
          <SambaAutoLink
            to={brochureUrl('/legal/form-crs')}
            target="_blank"
            onClick={() => trackClick({ name: 'UserAgreement' })}
          >
            Form CRS Relationship Summary
          </SambaAutoLink>
        </li>
        <li>
          <SambaAutoLink to={brochureUrl('/adv')} target="_blank" onClick={() => trackClick({ name: 'UserAgreement' })}>
            Form ADV brochure
          </SambaAutoLink>
        </li>
        <li>
          <SambaAutoLink
            to={urlBuilder.getPrivacyPolicy()}
            target="_blank"
            onClick={() => trackClick({ name: 'UserAgreement' })}
          >
            Betterment&apos;s Privacy Policy
          </SambaAutoLink>
        </li>
      </Box>
      <Text as="p" css={{ marginBottom: 'space.200', _last: { marginBottom: 'space.0' } }}>
        Your advisory agreement contains a pre-dispute clause highlighted in paragraph 31 on page 28. Your brokerage
        agreement contains a predispute arbitration clause highlighted in paragraph 36 on page 66.
      </Text>
    </>
  );
};

interface Props extends StepProps<FlowValues<CustomerFlowValues>> {
  documents: BankDocument[];
  user: User;
  handleClose: () => void;
}

const Disclosure: React.FC<React.PropsWithChildren<Props>> = ({
  flowState,
  documents,
  goToNextStep,
  user,
  handleClose,
}) => {
  const { isB4BOnly } = user;
  const { customer, stepTitle } = flowState.values;

  useEffect(() => {
    track('ElementViewed', { name: 'Disclosure', flow: 'OpenCheckingAccount' });
  }, []);

  if (customer && (isVerified(customer) || isManualReview(customer))) {
    locationAssign(urlBuilder.appHome.absolute());
    return <FullPageLoader />;
  }

  return (
    <WorkflowLayout
      feature="checking_customer_signup"
      pageTitle="Sign up - Checking"
      workflowTitle={stepTitle}
      onClose={handleClose}
    >
      <Heading variant="primary">Agree to Checking terms and conditions</Heading>
      <Formik onSubmit={goToNextStep} initialValues={flowState.values}>
        {({ isSubmitting }) => (
          <Form>
            <FlashErrorMessage name="user" />
            <Text as="p" css={{ marginBottom: 'space.200', _last: { marginBottom: 'space.0' } }}>
              The terms and conditions below are specific to your new Checking account.
            </Text>
            <Text
              as="p"
              css={{
                marginBottom: 'space.200',
                _last: { marginBottom: 'space.0' },
              }}
            >
              By clicking “Accept and continue,” you acknowledge that you have read and agreed to the following
              documents:
            </Text>
            <Box mb="9">
              {isB4BOnly && (
                <Text
                  as="p"
                  css={{
                    fontWeight: 'bold',
                    marginBottom: 'space.100',
                  }}
                >
                  nbkc bank AGREEMENTS
                </Text>
              )}
              <Box as="ul" m="0" mb="6" pl="7">
                {documents &&
                  documents.map(doc => (
                    <li key={doc.id}>
                      <DocumentLink {...doc} />
                    </li>
                  ))}
                <li>
                  <SambaAutoLink
                    target="_blank"
                    to={brochureUrl('/legal/everyday-checking')}
                    onClick={() => trackClick({ name: 'UserAgreement' })}
                  >
                    Betterment Financial Terms &amp; Conditions
                  </SambaAutoLink>
                </li>
                {!isB4BOnly && (
                  <li>
                    <SambaAutoLink
                      target="_blank"
                      to={brochureUrl('/legal/form-crs')}
                      onClick={() => trackClick({ name: 'UserAgreement' })}
                    >
                      Betterment Form CRS Relationship Summary
                    </SambaAutoLink>
                  </li>
                )}
                <li>
                  <SambaAutoLink
                    target="_blank"
                    to={brochureUrl('/legal/transaction-data-enhancement')}
                    onClick={() => trackClick({ name: 'UserAgreement' })}
                  >
                    MX Technologies User Agreement
                  </SambaAutoLink>
                </li>
              </Box>
              {isB4BOnly && <BettermentAgreements />}
            </Box>
            <Button
              disabled={isSubmitting}
              type="submit"
              px="9"
              autoFocus={true}
              onClick={() => trackClick({ name: 'ContinueCheckingSignup2' })}
            >
              Accept and continue
            </Button>
          </Form>
        )}
      </Formik>
    </WorkflowLayout>
  );
};

export default Disclosure;
